import type { ExtendedHighlight } from "@/api/highlights";
import type { HighlightMetaIndex } from "../types";

// Function to check if two highlights overlap
export const highlightsOverlap = (
  h1: ExtendedHighlight,
  h2: ExtendedHighlight,
): boolean => {
  // Only check highlights on the same page
  if (
    h1.position.boundingRect.pageNumber !== h2.position.boundingRect.pageNumber
  )
    return false;

  const rect1 = h1.position.boundingRect;
  const rect2 = h2.position.boundingRect;

  // Check for bounding box overlap
  const noOverlap =
    rect1.x2 < rect2.x1 ||
    rect2.x2 < rect1.x1 ||
    rect1.y2 < rect2.y1 ||
    rect2.y2 < rect1.y1;

  return !noOverlap;
};

// Function to merge overlapping highlights
export const mergeOverlappingHighlights = (
  highlights: ExtendedHighlight[],
  citationMap: Map<string, any>,
  metaIndex: HighlightMetaIndex,
): {
  mergedHighlights: ExtendedHighlight[];
  updatedCitationDataMap: Map<string, any>;
  updatedMetaIndex: HighlightMetaIndex;
} => {
  if (highlights.length <= 1)
    return {
      mergedHighlights: highlights,
      updatedCitationDataMap: citationMap,
      updatedMetaIndex: metaIndex,
    };

  const citationHighlights = highlights.filter((h) =>
    h.id.startsWith("citation-"),
  );
  const otherHighlights = highlights.filter(
    (h) => !h.id.startsWith("citation-"),
  );

  // Group citations by page + question + answer-group so merging only happens
  // within a single answer group. Signature Q&A puts each signer in its own
  // answer group within the same question — merging across answer groups
  // would combine adjacent signers' bboxes into one big highlight that covers
  // several people simultaneously (seen on 1009 Omnibus pages).
  const citationsByPageAndQuestion = new Map<string, ExtendedHighlight[]>();
  for (const highlight of citationHighlights) {
    const page = highlight.position.boundingRect.pageNumber;
    const data = citationMap.get(highlight.id);
    const questionId = data?.questionId ?? "unknown";
    const answerGroupId = data?.answerGroupId ?? "unknown";
    const key = `${page}:${questionId}:${answerGroupId}`;
    const group = citationsByPageAndQuestion.get(key) ?? [];
    group.push(highlight);
    citationsByPageAndQuestion.set(key, group);
  }

  const mergedCitations: ExtendedHighlight[] = [];
  const updatedCitationMap = new Map(citationMap);
  const updatedMeta = new Map(metaIndex.meta);
  const updatedByCitation = new Map(metaIndex.byCitation);

  // Process each page+question group separately
  citationsByPageAndQuestion.forEach((pageHighlights) => {
    const processed = new Set<string>();

    pageHighlights.forEach((highlight) => {
      if (processed.has(highlight.id)) return;

      // Find all overlapping highlights for this one
      const overlapping = [highlight];
      const overlappingQuestions = [highlight.comment?.text || ""];
      const overlappingFullQuestions: string[] = [];

      // Track all questionIds and answerGroupIds from constituent highlights
      const sourceQuestionIds = new Set<string>();
      const sourceAnswerGroupIds = new Set<string>();
      const sourceCitationIds: string[] = [];

      // Group answers by question to avoid duplicating question text
      const questionToAnswersMap = new Map<string, Array<{
        answerText: string;
        answerExplanation?: string;
        isCurrentValue?: boolean;
        isComposite?: boolean;
      }>>();

      // Get the full question text and answer for the first highlight
      const highlightData = citationMap.get(highlight.id);
      if (highlightData) {
        overlappingFullQuestions.push(highlightData.questionText);
        if (highlightData.questionId) sourceQuestionIds.add(highlightData.questionId);
        if (highlightData.answerGroupId) sourceAnswerGroupIds.add(highlightData.answerGroupId);

        // Initialize or add to the question's answers
        const existingAnswers = questionToAnswersMap.get(highlightData.questionText) || [];
        existingAnswers.push({
          answerText: highlightData.answerText,
          answerExplanation: highlightData.answerExplanation,
          isCurrentValue: highlightData.isCurrentValue,
          isComposite: highlightData.isComposite,
        });
        questionToAnswersMap.set(highlightData.questionText, existingAnswers);
      }

      // Collect citation IDs from the first highlight's meta
      const firstMeta = metaIndex.meta.get(highlight.id);
      if (firstMeta) {
        sourceCitationIds.push(...firstMeta.citationIds);
      }

      pageHighlights.forEach((other) => {
        if (
          other.id !== highlight.id &&
          !processed.has(other.id) &&
          highlightsOverlap(highlight, other)
        ) {
          overlapping.push(other);
          overlappingQuestions.push(other.comment?.text || "");

          // Get the full question text and answer for this overlapping highlight
          const otherData = citationMap.get(other.id);
          if (otherData) {
            // Only add question if we haven't seen it before
            if (!overlappingFullQuestions.includes(otherData.questionText)) {
              overlappingFullQuestions.push(otherData.questionText);
            }
            if (otherData.questionId) sourceQuestionIds.add(otherData.questionId);
            if (otherData.answerGroupId) sourceAnswerGroupIds.add(otherData.answerGroupId);

            // Add answer to the question's answers list
            const existingAnswers = questionToAnswersMap.get(otherData.questionText) || [];
            existingAnswers.push({
              answerText: otherData.answerText,
              answerExplanation: otherData.answerExplanation,
              isCurrentValue: otherData.isCurrentValue,
              isComposite: otherData.isComposite,
            });
            questionToAnswersMap.set(otherData.questionText, existingAnswers);
          }

          // Collect citation IDs from other highlight's meta
          const otherMeta = metaIndex.meta.get(other.id);
          if (otherMeta) {
            sourceCitationIds.push(...otherMeta.citationIds);
          }
        }
      });

      // Convert the grouped data to the format expected by the popup
      const groupedQuestionsAndAnswers = Array.from(questionToAnswersMap.entries()).map(([question, answers]) => ({
        question,
        answers,
      }));

      if (overlapping.length > 1) {
        // Use the first citation's bounding rect as the base, but extend it to cover all overlapping highlights
        const mergedBoundingRect = overlapping.reduce((merged, h) => {
          const rect = h.position.boundingRect;
          return {
            x1: Math.min(merged.x1, rect.x1),
            y1: Math.min(merged.y1, rect.y1),
            x2: Math.max(merged.x2, rect.x2),
            y2: Math.max(merged.y2, rect.y2),
            width: Math.max(merged.width, rect.width),
            height: Math.max(merged.height, rect.height),
            pageNumber: rect.pageNumber,
          };
        }, highlight.position.boundingRect);

        // Preserve "area" type if any merged highlight is an area highlight (scanned page)
        const mergedType = overlapping.some((h) => h.type === "area")
          ? ("area" as const)
          : ("text" as const);

        const mergedHighlight: ExtendedHighlight = {
          id: `merged-citation-${overlapping.map((h) => h.id.replace("citation-", "")).join("-")}`,
          type: mergedType,
          position: {
            boundingRect: mergedBoundingRect,
            rects: [mergedBoundingRect], // Use single merged rectangle instead of multiple rects
          },
          comment: {
            text: `${overlapping.length} citations`,
            emoji: "🔍",
          },
        };
        mergedCitations.push(mergedHighlight);

        // Store merged citation data for popup display
        updatedCitationMap.set(mergedHighlight.id, {
          questionIds: Array.from(sourceQuestionIds),
          answerGroupIds: Array.from(sourceAnswerGroupIds),
          questionText: `${overlapping.length} merged citations`,
          answerText: "Multiple answers combined",
          answerExplanation: "See individual questions below",
          questions: overlappingFullQuestions,
          groupedQuestionsAndAnswers: groupedQuestionsAndAnswers,
        });

        // Update meta index: create merged meta and remap byCitation
        const mergedMeta = {
          highlightId: mergedHighlight.id,
          citationIds: sourceCitationIds,
          questionIds: Array.from(sourceQuestionIds),
          answerGroupIds: Array.from(sourceAnswerGroupIds),
          kind: "merged-citation" as const,
        };
        updatedMeta.set(mergedHighlight.id, mergedMeta);

        // Remap all constituent citation IDs to point to the merged highlight
        for (const cId of sourceCitationIds) {
          updatedByCitation.set(cId, mergedHighlight.id);
        }

        // Remove old individual meta entries
        overlapping.forEach((h) => {
          updatedMeta.delete(h.id);
          processed.add(h.id);
        });
      } else {
        // No overlap, keep original
        mergedCitations.push(highlight);
        processed.add(highlight.id);
      }
    });
  });

  return {
    mergedHighlights: [...otherHighlights, ...mergedCitations],
    updatedCitationDataMap: updatedCitationMap,
    updatedMetaIndex: { meta: updatedMeta, byCitation: updatedByCitation },
  };
};
